import { useState, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEventEmitter, { emitEvent } from "../../../hooks/useEventEmitter";
import { fireSaveAndMoveNextTimelineEvent } from "./SaveAndMoveNextTimeline";
import useAuthValue from "../../authModule/hooks/useAuthValue";
import useSelectedHospitalValue from "../../hospitalModule/hooks/useSelectedHospitalValue";
import SideDrawer, { SideDrawerBody } from "../../../components/SideDrawer";
import InputText from "../../../components/InputText";
import Divider from "../../../components/Divider";
import useUpdateCaseMutation from "../hooks/useUpdateCaseMutation";
import isNotNull from "../../../helpers/isNotNull";
import { detectChange } from "../../../helpers/detectChange";
import { updateCaseSummaryCommentInCache } from "../hooks/useGetCaseSummaryQuery";
import EditCaseSummaryDrawerFooter from "./EditCaseSummaryDrawerFooter";

const EDIT_CASE_COMMENT_DRAWER_EVENT = "EDIT_CASE_COMMENT_DRAWER_EVENT";

interface EditCaseCommentDrawerProps {
  caseId?: number;
  comments?: string;
  timelineItemIndex?: number;
}

export const toggleEditCaseCommentDrawer = (
  data?: EditCaseCommentDrawerProps
) => {
  emitEvent(EDIT_CASE_COMMENT_DRAWER_EVENT, data);
};

const EditCaseCommentDrawer = () => {
  const { t } = useTranslation();
  const { tenantId, hospitalId } = useAuthValue();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<EditCaseCommentDrawerProps>();
  const { caseId, comments, timelineItemIndex } = detail ?? {};

  const currentHospital = useSelectedHospitalValue();
  const hospitalID = currentHospital?.uuid ?? hospitalId;

  const { mutate: updateCaseMutate, isPending: isUpdatingCase } =
    useUpdateCaseMutation();

  const { control, watch, reset, handleSubmit } = useForm({
    defaultValues: {
      comments: "",
    },
  });

  const onClose = useCallback(() => {
    reset();
    setIsDrawerOpen(false);
  }, [reset]);

  const setFormData = useCallback(
    (data?: EditCaseCommentDrawerProps) => {
      const formDefaultValues = {
        comments: data?.comments ?? "",
      };
      reset(formDefaultValues);
    },
    [reset]
  );

  useEventEmitter(
    EDIT_CASE_COMMENT_DRAWER_EVENT,
    (data?: EditCaseCommentDrawerProps) => {
      setIsDrawerOpen((prev) => !prev);
      setDetail(data);
      setFormData(data);
    }
  );

  const isSubmitDisabled = useMemo(() => {
    const isCommentsChanged = detectChange(comments, watch("comments"));
    return !watch("comments") || !isCommentsChanged || isUpdatingCase;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("comments"), comments, isUpdatingCase]);

  const onSubmit = (isSaveAndMoveNext?: boolean) => {
    handleSubmit((val) => {
      if (caseId && hospitalID && tenantId) {
        updateCaseMutate(
          {
            data: {
              id: caseId,
              comments: val.comments,
            },
            hospitalId: hospitalID,
            tenantId,
          },
          {
            onSuccess: () => {
              onClose();
              updateCaseSummaryCommentInCache({
                caseId,
                comments: val.comments,
              });
              if (isSaveAndMoveNext && isNotNull(timelineItemIndex)) {
                fireSaveAndMoveNextTimelineEvent(timelineItemIndex);
              }
            },
          }
        );
      }
    })();
  };

  return (
    <SideDrawer
      visible={isDrawerOpen}
      onClose={onClose}
      title={t("Edit_Case_Comments")}
    >
      <SideDrawerBody>
        {/* Content */}
        <InputText
          control={control}
          name="comments"
          label={t("Comments")}
          multiline
          rows={8}
          fullWidth
          autoFocus
          isLabelDefaultBehaviour
          sx={{ borderRadius: 1 }}
        />

        {/* Footer */}
        <Divider sx={{ mt: 8, mb: 2, mx: -3 }} />
        <EditCaseSummaryDrawerFooter
          onSubmit={onSubmit}
          isSubmitDisabled={isSubmitDisabled}
        />
      </SideDrawerBody>
    </SideDrawer>
  );
};

export default EditCaseCommentDrawer;
